import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { X, ArrowRight, ChevronDown, Check, CheckCircle2, AlertCircle, Maximize2 } from "lucide-react"
import { doc, onSnapshot } from "firebase/firestore"
import { db } from "../../firebaseConfig"
import { APPLY_AGENT_API, ResumeCanvas } from "./ApplicationsTab"

const FF = "'Plus Jakarta Sans', 'Inter', sans-serif"
const BRANDFETCH_CLIENT_ID = "1idn4glWklGs5yLvUdW"

// ──────────────────────────────────────────────────────────────────────────
// Shared helpers
// ──────────────────────────────────────────────────────────────────────────
const brandDomain = (company = "") =>
  company
    ? company.toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9]/g, "") + ".com"
    : null

const kwName = (k) =>
  typeof k === "string" ? k : (k?.keyword || k?.name || k?.label || k?.skill || k?.text || "")
const isHardKw = (k) => {
  const t = (k?.type || k?.kind || "").toString().toLowerCase()
  return t === "" || t === "hard" || t === "technical" || t === "required"
}
const isMatched = (k) => k?.matched === true || k?.matched === "true" || k?.covered === true

function CompanyLogo({ company, size = 32, radius = 8 }) {
  const domain = brandDomain(company)
  return (
    <div style={{
      width: size, height: size, borderRadius: radius,
      background: "#f8f9fa",
      border: "1px solid #ececec",
      overflow: "hidden", flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {domain ? (
        <img
          src={`https://cdn.brandfetch.io/${domain}?c=${BRANDFETCH_CLIENT_ID}`}
          alt={company}
          onError={e => { e.currentTarget.style.display = "none" }}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      ) : (
        <span style={{ fontSize: size * 0.4, fontWeight: 700, color: "#52525b", fontFamily: FF }}>
          {(company || "?")[0]?.toUpperCase()}
        </span>
      )}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Panel — wraps the pending-applications switcher + the focused flow view.
// Multiple Agent Apply clicks stack in `flows`; one is "focused" and
// rendered as the full FlowView; others are reachable via the top bar.
// ──────────────────────────────────────────────────────────────────────────
export default function AgentApplyPanel({
  userId,
  flows,
  focusedKey,
  onFocus,
  onCancel, // onCancel(jobKey)
  onApply,  // onApply(jobKey, variant, appData)
}) {
  const [listOpen, setListOpen] = useState(false)

  // If the focused flow goes away (apply or cancel), collapse the list.
  useEffect(() => { setListOpen(false) }, [focusedKey])

  const focused = flows.find(f => f.jobKey === focusedKey) || flows[0]
  const multi = flows.length > 1

  return (
    <div style={{
      display: "flex", flexDirection: "column",
      height: "100%",
      padding: "16px 18px 20px",
      fontFamily: FF,
      background: "#ffffff",
      gap: 16,
    }}>
      <style>{`
        @keyframes agent-fade {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes agent-indet {
          0%   { transform: translateX(-120%); }
          100% { transform: translateX(360%); }
        }
        @keyframes agent-pulse-dot {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%      { transform: scale(1.4); opacity: 0.5; }
        }
        @keyframes agent-row-in {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes agent-td-blink {
          0%, 80%, 100% { opacity: 0.15; transform: translateY(0); }
          40%            { opacity: 1;    transform: translateY(-1px); }
        }
        @keyframes agent-compile-drop {
          0% { opacity: 0; transform: translateY(-8px); }
          25% { opacity: 1; }
          80% { opacity: 1; }
          100% { opacity: 0; transform: translateY(20px); }
        }
        @keyframes agent-editor-caret {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
        @keyframes agent-resume-shimmer {
          from { background-position: 200% 0; }
          to { background-position: -200% 0; }
        }
        .agent-resume-shimmer {
          background: linear-gradient(90deg, #e3e5e8 20%, #f7f8f9 50%, #e3e5e8 80%);
          background-size: 200% 100%;
          animation: agent-resume-shimmer 2.2s linear infinite;
        }
        .agent-row { animation: agent-row-in 0.28s cubic-bezier(0.22, 1, 0.36, 1) both; }
        .agent-timeline {
          display: flex; flex-direction: column;
          padding: 2px 2px 0;
        }
        .agent-td {
          display: inline-block;
          font-size: 14px; line-height: 1;
          color: currentColor;
          animation: agent-td-blink 1.1s ease-in-out infinite both;
        }
        .agent-toggle-pill {
          position: relative;
          display: inline-flex;
          padding: 3px;
          background: #f6f6f7;
          border-radius: 9999px;
          width: 100%;
        }
        .agent-toggle-btn {
          position: relative;
          flex: 1;
          height: 28px;
          display: inline-flex; align-items: center; justify-content: center;
          border: none; background: transparent; cursor: pointer;
          font-family: ${FF}; font-size: 11.5px; font-weight: 600;
          color: #86909c;
          border-radius: 9999px;
          transition: color 160ms ease;
          z-index: 1;
        }
        .agent-toggle-btn.on { color: #18181b; }
        .agent-toggle-btn:disabled { cursor: not-allowed; opacity: 0.5; }
        .agent-toggle-thumb {
          position: absolute;
          top: 3px; bottom: 3px;
          width: calc(50% - 3px);
          background: #ffffff;
          border-radius: 9999px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 0 0 0.5px rgba(0,0,0,0.04);
          transition: transform 240ms cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 0;
        }
        .agent-cta-primary {
          background: #18181b; color: #fff; border: none; cursor: pointer;
          transition: background 0.15s ease, transform 0.1s ease;
        }
        .agent-cta-primary:hover { background: #000; }
        .agent-cta-primary:active { transform: translateY(1px); }
        .agent-cta-ghost {
          background: transparent; color: #86909c; border: none; cursor: pointer;
          transition: color 0.12s ease;
        }
        .agent-cta-ghost:hover { color: #18181b; }
        .agent-resume-card {
          position: relative;
          display: block;
          width: 100%;
          padding: 10px;
          background: #ffffff;
          border: 1px solid #ececec;
          border-radius: 12px;
          cursor: zoom-in;
          overflow: hidden;
          transition: border-color 0.12s ease, box-shadow 0.12s ease;
        }
        .agent-resume-card:hover {
          border-color: #d4d4d8;
          box-shadow: 0 4px 14px rgba(18,18,18,0.06);
        }
        .agent-resume-card:hover .agent-resume-expand { opacity: 1; }
        .agent-resume-expand {
          position: absolute;
          top: 10px; right: 10px;
          width: 28px; height: 28px;
          display: inline-flex; align-items: center; justify-content: center;
          background: rgba(24,24,27,0.82);
          color: #ffffff;
          border-radius: 8px;
          opacity: 0;
          transition: opacity 0.14s ease;
          pointer-events: none;
          backdrop-filter: blur(6px);
        }
        .agent-resume-card .react-pdf__Document { display: block; }
        .agent-resume-card .react-pdf__Page { background: transparent !important; }
        .agent-resume-card .react-pdf__Page canvas {
          width: 100% !important;
          height: auto !important;
          border-radius: 6px;
        }
        .agent-scroll {
          flex: 1; min-height: 0; overflow-y: auto;
          margin: 0 -4px; padding: 0 4px;
          scrollbar-width: thin; scrollbar-color: #e4e4e7 transparent;
        }
        .agent-scroll::-webkit-scrollbar { width: 4px; }
        .agent-scroll::-webkit-scrollbar-thumb { background: #e4e4e7; border-radius: 2px; }
        .agent-pending-bar {
          display: flex; align-items: center; gap: 8px;
          width: 100%;
          padding: 6px 2px 10px;
          background: transparent;
          border: none;
          border-bottom: 1px solid #f1f3f5;
          cursor: pointer;
          font-family: ${FF};
          transition: color 0.12s ease;
        }
        .agent-pending-bar:hover .agent-pending-chevron { color: #18181b; }
        .agent-pending-row {
          display: flex; align-items: center; gap: 10px;
          width: 100%;
          padding: 8px 6px;
          background: transparent;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          text-align: left;
          font-family: ${FF};
          transition: background 0.12s ease;
        }
        .agent-pending-row:hover { background: #fafafb; }
        .agent-pending-row.on { background: #f6f6f7; }
        .agent-editor-scroll::-webkit-scrollbar,
        .agent-preview-scroll::-webkit-scrollbar { display: none; }
        @media (prefers-reduced-motion: reduce) {
          .agent-optimising-hero { animation: none !important; }
          .agent-optimising-visual * {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>

      {/* ── Pending applications switcher (only when 2+ in flight) ── */}
      {multi && (
        <>
          <button
            type="button"
            className="agent-pending-bar"
            onClick={() => setListOpen(v => !v)}
            aria-expanded={listOpen}
          >
            <span style={{
              flex: 1, textAlign: "left",
              display: "inline-flex", alignItems: "baseline", gap: 6,
              fontFamily: FF,
            }}>
              <span style={{
                fontSize: 9.5, fontWeight: 700, color: "#a1a1aa",
                textTransform: "uppercase", letterSpacing: "0.1em",
              }}>
                Pending Applications
              </span>
              <span style={{
                fontSize: 11, fontWeight: 700, color: "#52525b",
                fontVariantNumeric: "tabular-nums",
              }}>
                · {flows.length}
              </span>
            </span>
            <ChevronDown
              size={13}
              strokeWidth={2.2}
              className="agent-pending-chevron"
              color="#a1a1aa"
              style={{
                transition: "transform 180ms ease, color 0.12s ease",
                transform: listOpen ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </button>

          {listOpen && (
            <div style={{
              display: "flex", flexDirection: "column", gap: 2,
              padding: "4px 2px",
              animation: "agent-fade 0.2s ease both",
            }}>
              {flows.map(f => (
                <PendingRow
                  key={f.jobKey}
                  flow={f}
                  userId={userId}
                  active={f.jobKey === focused?.jobKey}
                  onSelect={() => { onFocus?.(f.jobKey); setListOpen(false) }}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* ── Flow views ──
          We render every flow (not just the focused one) and hide the
          non-focused ones with display:none so each FlowView keeps its
          state — SSE connections, event log, submitted phase — alive
          across focus changes. Switching back to an "applying" flow then
          shows the running log, not a reset Optimising panel. */}
      {flows.map(f => {
        const show = !listOpen && f.jobKey === focused?.jobKey
        return (
          <div
            key={f.jobKey}
            style={{
              display: show ? "flex" : "none",
              flex: show ? 1 : "0 0 auto",
              minHeight: 0,
              flexDirection: "column",
            }}
          >
            <FlowView
              flow={f}
              userId={userId}
              isFocused={show}
              onCancel={() => onCancel?.(f.jobKey)}
              onApply={(variant, appData) => onApply?.(f.jobKey, variant, appData)}
            />
          </div>
        )
      })}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// PendingRow — a compact row in the dropdown switcher.
// Subscribes to its own appDoc so we can show live status (Optimising / Ready).
// ──────────────────────────────────────────────────────────────────────────
function PendingRow({ flow, userId, active, onSelect }) {
  const [status, setStatus] = useState("")
  const [hasOptData, setHasOptData] = useState(false)
  const [hasResumeUrl, setHasResumeUrl] = useState(false)

  useEffect(() => {
    setStatus("")
    setHasOptData(false)
    setHasResumeUrl(false)
    if (!userId || !flow?.appId) return
    const unsub = onSnapshot(
      doc(db, "users", userId, "applications", flow.appId),
      snap => {
        if (!snap.exists()) return
        const data = snap.data() || {}
        setStatus(String(data.status || ""))
        setHasOptData(Boolean(data.optimizationData))
        setHasResumeUrl(Boolean(data.resumeUrl))
      },
      err => { console.error("PendingRow subscribe:", err) },
    )
    return () => unsub()
  }, [userId, flow?.appId])

  const label = (() => {
    if (!flow.appId) return "Starting"
    if (status === "pending_resume_review" && hasOptData && hasResumeUrl) return "Ready"
    if (status === "pending_optimization" || status === "") return "Optimising"
    if (status === "additional_information_required") return "Needs input"
    if (status === "pending_verification") return "Verify email"
    if (status === "credentials_error") return "Reset password"
    if (status === "queued" || status === "processing") return "Applying"
    if (status === "completed") return "Submitted"
    if (status === "failed") return "Paused"
    return "Optimising"
  })()

  const ready = label === "Ready"

  return (
    <button
      type="button"
      className={`agent-pending-row${active ? " on" : ""}`}
      onClick={onSelect}
    >
      <CompanyLogo company={flow.job?.company || ""} size={28} radius={7} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 12, fontWeight: 700, color: "#18181b",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          lineHeight: 1.3,
        }}>
          {flow.job?.role || flow.job?.title || "Role"}
        </div>
        <div style={{
          marginTop: 1,
          fontSize: 10.5, fontWeight: 500, color: "#86909c",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {flow.job?.company || "Company"}
          <span style={{ color: "#d4d4d8", margin: "0 5px" }}>·</span>
          <span style={{
            color: ready ? "#0f7a4a" : "#52525b",
            fontWeight: ready ? 700 : 500,
          }}>
            {label}
          </span>
        </div>
      </div>
      {active && (
        <span style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 14, height: 14,
          color: "#52525b",
          flexShrink: 0,
        }}>
          <Check size={11} strokeWidth={2.4} />
        </span>
      )}
    </button>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// FlowView — the single-flow UI: company header → optimising state or
// ready state (Optimised/Default preview + CTAs). Owns its own appDoc
// subscription and countdown.
// ──────────────────────────────────────────────────────────────────────────
function FlowView({ flow, userId, isFocused = true, onCancel, onApply }) {
  const { job, appId, defaultResumeUrl } = flow
  const [appDoc, setAppDoc] = useState(null)
  const [variant, setVariant] = useState("optimised")
  const [fullscreen, setFullscreen] = useState(false)
  // Submitted = the user has approved a resume variant; the panel now shows
  // live /apply/events instead of the preview.
  const [submitted, setSubmitted] = useState(false)
  const [events, setEvents] = useState([])
  const [applyError, setApplyError] = useState(false)
  // Once we see a terminal event (done / cancelled / failure in the stream)
  // we close the SSE and stop reopening — otherwise the browser's built-in
  // auto-reconnect replays the entire history and the log duplicates
  // forever.
  const [terminal, setTerminal] = useState(null) // null | "done" | "cancelled" | "failed"
  const scrollRef = useRef(null)

  useEffect(() => {
    setAppDoc(null)
    setVariant("optimised")
    setSubmitted(false)
    setEvents([])
    setApplyError(false)
    setTerminal(null)
    setFullscreen(false)
  }, [flow.jobKey])

  useEffect(() => {
    if (!userId || !appId) return
    const unsub = onSnapshot(
      doc(db, "users", userId, "applications", appId),
      snap => { if (snap.exists()) setAppDoc({ id: snap.id, ...snap.data() }) },
      err => { console.error("FlowView subscribe:", err) },
    )
    return () => unsub()
  }, [userId, appId])

  const status = String(appDoc?.status || "")
  const ready = status === "pending_resume_review" && appDoc?.resumeUrl && appDoc?.optimizationData
  const sessionId = appDoc?.sessionId || ""

  // Live progress event stream — opens once we've approved and the backend
  // has written a sessionId onto the app doc. We bail if the stream has
  // already hit a terminal state so the browser's EventSource auto-reconnect
  // doesn't resurrect it and replay the whole log.
  useEffect(() => {
    if (!submitted || !sessionId || terminal) return
    const es = new EventSource(`${APPLY_AGENT_API}/apply/events/${sessionId}`)
    let closed = false
    const safeClose = (reason) => {
      if (closed) return
      closed = true
      es.close()
      if (reason) setTerminal(reason)
    }

    es.onmessage = (e) => {
      if (!e.data || e.data === "[DONE]") return
      try {
        const ev = JSON.parse(e.data)
        const row = formatProgressEvent(ev)
        if (!row) return

        // Dedupe: if the same label is already the latest row, skip it so
        // reconnects (which replay history) don't pile up.
        setEvents(prev => {
          const last = prev[prev.length - 1]
          if (last && last.label === row.label && last.kind === row.kind) return prev
          return [...prev, { id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, ...row }]
        })

        // Terminal detection — close the stream so EventSource doesn't
        // auto-reconnect.
        if (ev.type === "done") safeClose("done")
        else if (ev.type === "cancelled") safeClose("cancelled")
        else if (ev.type === "error" || row.kind === "error") safeClose("failed")
        else if (/\b(did not complete|unsuccessful|failed to|failed\b)/i.test(row.label)) safeClose("failed")
      } catch {}
    }

    es.onerror = () => {
      // Append a single soft "Reconnecting…" row (not stacked). If we're
      // already in a terminal state the useEffect-guard above has us
      // covered — this fires once from the initial close and we close
      // the socket here too to prevent further reconnect noise.
      setEvents(prev => prev.length && prev[prev.length - 1].kind === "reconnecting"
        ? prev
        : [...prev, { id: `rc-${Date.now()}`, label: "Reconnecting…", kind: "reconnecting" }])
    }

    return () => safeClose(null)
  }, [submitted, sessionId, terminal])

  // Auto-scroll the event log as new rows arrive, and also whenever this
  // flow comes back into focus (scrollHeight is 0 while hidden with
  // display:none, so events that arrived in the background need to be
  // scrolled into view once the user switches to this flow).
  useEffect(() => {
    if (!scrollRef.current || !isFocused) return
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [events.length, isFocused])

  const handleApplyClick = async (v) => {
    setApplyError(false)
    setSubmitted(true)
    setEvents([{ id: `start-${Date.now()}`, label: "Starting application…", kind: "pending" }])
    try {
      await onApply?.(v, appDoc)
    } catch {
      setApplyError(true)
      setSubmitted(false)
      setEvents([])
    }
  }

  const company = job?.company || "Company"
  const title = job?.role || job?.title || "This role"

  const meta = appDoc?.optimizationData?.optimizationMetadata || {}
  const keywords = Array.isArray(meta.keywords) ? meta.keywords : []
  const hardKeywords = keywords.filter(isHardKw)
  const hardCovered = hardKeywords.filter(isMatched)
  const hardMissing = hardKeywords.filter(k => !isMatched(k))
  const percentage = Number(meta.percentage || 0)
  const matchedCount = Number(meta.matched || keywords.filter(isMatched).length || 0)
  const totalCount = Number(meta.total || keywords.length || 0)
  const changes = Array.isArray(meta.changes) ? meta.changes : []

  const optimisedPdfUrl = appDoc?.resumeUrl || ""
  const activePdfUrl = variant === "optimised" ? optimisedPdfUrl : defaultResumeUrl

  return (
    <div style={{
      display: "flex", flexDirection: "column", gap: 16,
      flex: 1, minHeight: 0,
      animation: "agent-fade 0.2s ease both",
    }}>
      {/* ── Company header row ── */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <CompanyLogo company={company} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 13, fontWeight: 700, color: "#18181b",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>{title}</div>
          <div style={{
            marginTop: 2, fontSize: 11, color: "#86909c", fontWeight: 500,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>{company}</div>
        </div>
        <button
          onClick={onCancel}
          aria-label="Cancel"
          style={{
            width: 28, height: 28, borderRadius: 8,
            background: "transparent", border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#a1a1aa",
            transition: "color 0.12s, background 0.12s",
            flexShrink: 0,
          }}
          onMouseEnter={e => { e.currentTarget.style.color = "#18181b"; e.currentTarget.style.background = "#f6f6f7" }}
          onMouseLeave={e => { e.currentTarget.style.color = "#a1a1aa"; e.currentTarget.style.background = "transparent" }}
        >
          <X size={15} strokeWidth={2.2} />
        </button>
      </div>

      {/* ── Running state — live event stream after the user approved ── */}
      {submitted && (
        <RunningView
          events={events}
          terminal={terminal}
          scrollRef={scrollRef}
          onClose={onCancel}
        />
      )}

      {/* ── Optimising state ── */}
      {!submitted && !ready && (
        <OptimisingHero
          title={title}
          company={company}
        />
      )}

      {/* ── Ready state ── */}
      {!submitted && ready && (
        <>
          <div className="agent-toggle-pill" role="tablist">
            <span
              className="agent-toggle-thumb"
              style={{ transform: variant === "optimised" ? "translateX(0)" : "translateX(calc(100% + 3px))" }}
            />
            <button
              className={`agent-toggle-btn${variant === "optimised" ? " on" : ""}`}
              onClick={() => setVariant("optimised")}
              role="tab" aria-selected={variant === "optimised"}
              disabled={!optimisedPdfUrl}
            >
              Optimised
            </button>
            <button
              className={`agent-toggle-btn${variant === "default" ? " on" : ""}`}
              onClick={() => setVariant("default")}
              role="tab" aria-selected={variant === "default"}
              disabled={!defaultResumeUrl}
            >
              Default
            </button>
          </div>

          <div className="agent-scroll" key={variant}
            style={{ display: "flex", flexDirection: "column", gap: 22, animation: "agent-fade 0.25s ease both" }}
          >
            {variant === "optimised" ? (
              <OptimisedSummary
                percentage={percentage}
                matchedCount={matchedCount}
                totalCount={totalCount}
                hardCovered={hardCovered}
                hardMissing={hardMissing}
                changes={changes}
              />
            ) : (
              <DefaultSummary />
            )}

            {activePdfUrl && (
              <ResumePreviewCard
                url={activePdfUrl}
                onOpen={() => setFullscreen(true)}
              />
            )}
          </div>

          {fullscreen && activePdfUrl && (
            <ResumeFullscreenModal
              url={activePdfUrl}
              title={`${title}${company ? ` · ${company}` : ""}`}
              onClose={() => setFullscreen(false)}
            />
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <button
              onClick={() => handleApplyClick(variant)}
              className="agent-cta-primary"
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                gap: 8, height: 42, padding: "0 16px",
                borderRadius: 10, fontSize: 13, fontWeight: 600, fontFamily: FF,
              }}
            >
              Apply with {variant === "optimised" ? "optimised" : "default"} resume
              <ArrowRight size={14} strokeWidth={2.2} />
            </button>
            {applyError && (
              <div style={{
                fontSize: 10.5, color: "#b4421c", fontWeight: 500, textAlign: "center",
                marginTop: 2,
              }}>
                Couldn't start the agent — try again
              </div>
            )}
            <button
              onClick={onCancel}
              className="agent-cta-ghost"
              style={{ height: 30, fontSize: 11.5, fontWeight: 500, fontFamily: FF }}
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// OptimisingHero — a 30-second illustrated LaTeX draft, section by section.
// The live flow resolves from Firestore when the optimised resume is ready.
// ──────────────────────────────────────────────────────────────────────────
const DRAFT_LINES = [
  { section: "Profile", source: "\\section{Profile}", preview: "Profile", kind: "heading" },
  { section: "Profile", source: "{Profile} {line 1}", preview: true, kind: "body", widths: [92, 68] },
  { section: "Profile", source: "{Profile} {line 2}", preview: true, kind: "body", widths: [81] },
  { section: "Experience", source: "\\section{Experience}", preview: "Experience", kind: "heading" },
  { section: "Experience", source: "\\begin{itemize}" },
  { section: "Experience", source: "\\item {Experience} {bullet 1}", preview: true, kind: "bullet", widths: [89, 63] },
  { section: "Experience", source: "\\item {Experience} {bullet 2}", preview: true, kind: "bullet", widths: [74, 88] },
  { section: "Experience", source: "\\item {Experience} {bullet 3}", preview: true, kind: "bullet", widths: [95, 56] },
  { section: "Experience", source: "\\end{itemize}" },
  { section: "Skills", source: "\\section{Skills}", preview: "Skills", kind: "heading" },
  { section: "Skills", source: "{Skills} {group 1}", preview: true, kind: "body", widths: [72] },
  { section: "Skills", source: "{Skills} {group 2}", preview: true, kind: "body", widths: [84] },
  { section: "Projects", source: "\\section{Projects}", preview: "Projects", kind: "heading" },
  { section: "Projects", source: "\\begin{itemize}" },
  { section: "Projects", source: "\\item {Project} {bullet 1}", preview: true, kind: "bullet", widths: [82, 61] },
  { section: "Projects", source: "\\item {Project} {bullet 2}", preview: true, kind: "bullet", widths: [91, 72] },
  { section: "Projects", source: "\\end{itemize}" },
]
const DRAFT_SECTIONS = [
  { name: "Profile", start: 0, end: 3 },
  { name: "Experience", start: 3, end: 9 },
  { name: "Skills", start: 9, end: 12 },
  { name: "Projects", start: 12, end: 17 },
]
const DRAFT_DURATION_MS = 30000
const DRAFT_LINE_MS = DRAFT_DURATION_MS / DRAFT_LINES.length

function OptimisingHero({ title, company }) {
  const [elapsedMs, setElapsedMs] = useState(0)
  const [reducedMotion] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  )
  const editorRef = useRef(null)
  const previewRef = useRef(null)

  useEffect(() => {
    const startedAt = Date.now()
    const timer = setInterval(() => {
      const elapsed = Math.min(DRAFT_DURATION_MS, Date.now() - startedAt)
      setElapsedMs(elapsed)
      if (elapsed === DRAFT_DURATION_MS) clearInterval(timer)
    }, reducedMotion ? 1000 : 70)
    return () => clearInterval(timer)
  }, [reducedMotion])

  const activeLine = Math.min(DRAFT_LINES.length - 1, Math.floor(elapsedMs / DRAFT_LINE_MS))
  const activeSection = DRAFT_LINES[activeLine].section
  const secondsLeft = Math.max(0, Math.ceil((DRAFT_DURATION_MS - elapsedMs) / 1000))
  const previewLines = DRAFT_LINES.filter((line, index) =>
    line.preview && (reducedMotion || elapsedMs >= (index + 1) * DRAFT_LINE_MS)
  )

  useEffect(() => {
    if (reducedMotion || !editorRef.current) return
    editorRef.current.scrollTop = editorRef.current.scrollHeight
  }, [activeLine, reducedMotion])

  useEffect(() => {
    if (reducedMotion || !previewRef.current) return
    previewRef.current.scrollTop = previewRef.current.scrollHeight
  }, [previewLines.length, reducedMotion])

  return (
    <div className="agent-optimising-hero" style={{ display: "flex", flexDirection: "column", gap: 15, padding: "4px 2px", animation: "agent-fade 0.25s ease both" }}>
      <div className="agent-optimising-visual" aria-hidden="true" style={{
        padding: 10, borderRadius: 14, background: "#f8f8f8", border: "1px solid #ebebeb",
      }}>
        <div style={{ border: "1px solid #d7d7d7", borderRadius: 9, background: "#fff", overflow: "hidden", boxShadow: "0 3px 9px rgba(0,0,0,0.045)" }}>
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between", height: 31,
            padding: "0 10px", borderBottom: "1px solid #dedede",
            background: "linear-gradient(#f9f9f9, #efefef)",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 9,
          }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 9 }}>
              <span style={{ display: "inline-flex", gap: 5 }}>
                {["#ff5f57", "#febc2e", "#28c840"].map(color => (
                  <span key={color} style={{ width: 8, height: 8, borderRadius: "50%", background: color, border: "1px solid rgba(0,0,0,0.09)" }} />
                ))}
              </span>
              <span style={{ fontWeight: 700, color: "#4a4a4a" }}>resume.tex</span>
            </span>
            <span style={{ color: "#888" }}>LaTeX</span>
          </div>
          <div ref={editorRef} className="agent-editor-scroll" style={{
            height: 145, overflowY: "auto", overflowX: "hidden", padding: "8px 4px 10px",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 9, lineHeight: "17px", whiteSpace: "pre", scrollbarWidth: "none",
          }}>
            {(reducedMotion ? DRAFT_LINES : DRAFT_LINES.slice(0, activeLine + 1)).map((line, index) => {
              const progress = Math.max(0, Math.min(1, (elapsedMs - index * DRAFT_LINE_MS) / DRAFT_LINE_MS))
              const visible = reducedMotion ? line.source : line.source.slice(0, Math.floor(line.source.length * progress))
              const command = visible.match(/^\\[a-z]+/)?.[0] || ""
              const active = !reducedMotion && elapsedMs < DRAFT_DURATION_MS && index === activeLine
              return (
                <div key={index} style={{
                  display: "flex", gap: 7, margin: "0 -4px", padding: "0 4px",
                  background: active ? "#edf3f9" : "transparent",
                }}>
                  <span style={{ width: 18, color: "#adb3bb", textAlign: "right", flexShrink: 0 }}>{String(index + 1).padStart(2, "0")}</span>
                  <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "clip", color: "#373737" }}>
                    <span style={{ color: "#4a6790" }}>{command}</span>{visible.slice(command.length)}
                    {active && <span style={{ color: "#4a6790", animation: "agent-editor-caret 0.7s steps(1) infinite" }}>▌</span>}
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        <div style={{ height: 28, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ position: "absolute", top: 2, bottom: 2, width: 1, background: "#d4d4d4" }} />
          {elapsedMs < DRAFT_DURATION_MS && !reducedMotion && <span style={{
            position: "absolute", top: 1, width: 5, height: 5, borderRadius: "50%",
            background: "#707070", animation: "agent-compile-drop 1.9s ease-in-out infinite",
          }} />}
        </div>

        <div style={{ border: "1px solid #dedede", borderRadius: 7, background: "#fff", overflow: "hidden", boxShadow: "0 7px 18px rgba(0,0,0,0.055)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "9px 12px 0", fontSize: 8, fontWeight: 700, letterSpacing: "0.08em", color: "#999" }}>
            <span>RESUME DRAFT</span><span>PDF PREVIEW</span>
          </div>
          <div ref={previewRef} className="agent-preview-scroll" style={{
            height: 175, overflowY: "auto", padding: "4px 13px 12px",
            scrollbarWidth: "none", fontFamily: "Georgia, serif", color: "#303030",
          }}>
            <div style={{ padding: "5px 0 7px", borderBottom: "1px solid #ededed" }}>
              <div className="agent-resume-shimmer" style={{ width: "53%", height: 7, borderRadius: 2 }} />
              <div className="agent-resume-shimmer" style={{ width: "72%", height: 3, borderRadius: 2, marginTop: 6 }} />
            </div>
            {previewLines.map((line, index) => (
              <div key={`${line.section}-${index}`} style={{
                animation: reducedMotion ? "none" : "agent-row-in 0.35s ease both",
                ...(line.kind === "heading" ? {
                  marginTop: index === 0 ? 4 : 15, paddingBottom: 4,
                  borderBottom: "1px solid #d9d9d9", fontSize: 11, fontWeight: 700,
                } : line.kind === "bullet" ? {
                  display: "flex", gap: 6, marginTop: 6, fontSize: 9.5, lineHeight: 1.3,
                } : { marginTop: 5, fontSize: 9.5, lineHeight: 1.3 }),
              }}>
                {line.kind === "bullet" && <span style={{ flexShrink: 0 }}>•</span>}
                {line.kind === "heading" ? (
                  <span>{line.preview}</span>
                ) : (
                  <span style={{ display: "flex", flexDirection: "column", gap: 4, width: "100%", minWidth: 0 }}>
                    {line.widths.map((width, lineIndex) => (
                      <span key={lineIndex} className="agent-resume-shimmer" style={{
                        display: "block", width: `${width}%`, height: 4,
                        borderRadius: 2,
                      }} />
                    ))}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        <div role="status" style={{ fontSize: 13, fontWeight: 700, color: "#18181b", letterSpacing: "-0.01em", lineHeight: 1.3 }}>
          Optimising resume
        </div>
        <div style={{ fontSize: 11, color: "#86909c", lineHeight: 1.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          For {title}{company ? ` at ${company}` : ""}
        </div>
        <div style={{ marginTop: 4, fontSize: 10.5, fontWeight: 600, color: "#52525b" }}>
          {elapsedMs === DRAFT_DURATION_MS ? "Reviewing final draft" : `Drafting ${activeSection.toLowerCase()} · ${DRAFT_SECTIONS.findIndex(section => section.name === activeSection) + 1} of 4`}
        </div>
      </div>

      <div style={{ display: "flex", gap: 4 }} aria-hidden="true">
        {DRAFT_SECTIONS.map(section => {
          const start = section.start * DRAFT_LINE_MS
          const length = (section.end - section.start) * DRAFT_LINE_MS
          const progress = reducedMotion ? 1 : Math.max(0, Math.min(1, (elapsedMs - start) / length))
          return (
            <div key={section.name} style={{ flex: 1, minWidth: 0 }}>
              <div style={{ height: 3, borderRadius: 3, background: "#ececec", overflow: "hidden" }}>
                <div style={{ width: `${progress * 100}%`, height: "100%", background: "#636363" }} />
              </div>
              <div style={{ marginTop: 5, fontSize: 8.5, color: "#a1a1aa", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{section.name}</div>
            </div>
          )
        })}
      </div>

      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
        paddingTop: 10, borderTop: "1px solid #f0f0f0", fontSize: 10.5, color: "#86909c", fontWeight: 500,
      }}>
        <span>It may take up to 30 sec</span>
        <span role="timer" aria-live="off" style={{ color: "#52525b", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
          {secondsLeft > 0 ? `${secondsLeft}s left` : "Almost there…"}
        </span>
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// RunningView — live event log while the agent is driving the application.
// Replaces the resume-review content once the user has approved.
// ──────────────────────────────────────────────────────────────────────────
function RunningView({ events, terminal, scrollRef, onClose }) {
  const last = events[events.length - 1]
  const done = terminal === "done"
  const failed = terminal === "failed" || terminal === "cancelled"

  const heroLabel = done
    ? "Application submitted"
    : failed
      ? (terminal === "cancelled" ? "Application cancelled" : "Application paused")
      : (last && last.kind !== "reconnecting" ? last.label : "Starting application…")

  const heroHint = done
    ? "The agent has finished — check Applications for the record."
    : failed
      ? "The agent couldn't finish. You can retry from the Applications tab."
      : "The agent is driving the form. Each step shows up below as it happens."

  return (
    <>
      {/* Hero */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {done ? (
            <CheckCircle2 size={16} strokeWidth={2.2} color="#0f7a4a" />
          ) : failed ? (
            <AlertCircle size={16} strokeWidth={2.2} color="#b4421c" />
          ) : (
            <span style={{
              width: 7, height: 7, borderRadius: "50%",
              background: "#18181b",
              animation: "agent-pulse-dot 1.1s ease-in-out infinite",
              display: "inline-block",
            }} />
          )}
          <div style={{
            fontSize: 13, fontWeight: 700, color: "#18181b",
            letterSpacing: "-0.01em", lineHeight: 1.25,
          }}>
            {heroLabel}
          </div>
        </div>
        <div style={{
          fontSize: 11, color: "#86909c", lineHeight: 1.5,
        }}>
          {heroHint}
        </div>
      </div>

      {/* Event log — vertical timeline with a rail connecting the dots */}
      <div
        ref={scrollRef}
        className="agent-scroll agent-timeline"
      >
        {events.map((ev, i) => {
          const isLast = i === events.length - 1
          const isReconnecting = ev.kind === "reconnecting"
          const isError = ev.kind === "error" || ev.kind === "cancelled"
          const isPending = isLast && !terminal && !isReconnecting && !isError
          const isDone = !isPending && !isReconnecting && !isError
          return (
            <div
              key={ev.id}
              className="agent-row"
              style={{
                display: "flex", alignItems: "flex-start", gap: 10,
                position: "relative",
                padding: "0 0 10px 0",
                animationDelay: `${Math.min(i, 3) * 40}ms`,
              }}
            >
              {/* Connecting rail */}
              {i < events.length - 1 && (
                <span
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: 7, top: 16, bottom: 0,
                    width: 1,
                    background: "#f1f3f5",
                  }}
                />
              )}

              {/* Dot */}
              <div style={{
                width: 15, height: 15, borderRadius: "50%",
                marginTop: 2,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: isPending ? "#ffffff" : (isError ? "#ffffff" : (isReconnecting ? "transparent" : "#18181b")),
                border: isPending
                  ? "1.5px solid #18181b"
                  : isError
                    ? "1.5px solid #fde5db"
                    : (isReconnecting ? "1px dashed #e4e4e7" : "none"),
                color: "#fff",
                flexShrink: 0,
                position: "relative",
                zIndex: 1,
                boxShadow: isPending ? "0 0 0 3px rgba(24,24,27,0.06)" : "none",
                transition: "background 180ms ease, border-color 180ms ease, box-shadow 180ms ease",
              }}>
                {isPending && (
                  <span style={{
                    width: 5, height: 5, borderRadius: "50%",
                    background: "#18181b",
                    animation: "agent-pulse-dot 1s ease-in-out infinite",
                  }} />
                )}
                {isDone && <Check size={8} strokeWidth={3.5} />}
                {isError && (
                  <span style={{
                    width: 5, height: 5, borderRadius: "50%",
                    background: "#b4421c",
                  }} />
                )}
              </div>

              {/* Label */}
              <div style={{
                flex: 1, minWidth: 0,
                fontSize: 12,
                fontWeight: isPending ? 600 : 500,
                color: isPending
                  ? "#18181b"
                  : isError
                    ? "#b4421c"
                    : (isReconnecting ? "#a1a1aa" : "#52525b"),
                lineHeight: 1.5,
                wordBreak: "break-word",
              }}>
                {ev.label}
                {isPending && <TypingDots />}
              </div>
            </div>
          )
        })}

        {/* "Agent is thinking" row — only while we're actively running and
            there's no event in flight yet. */}
        {!terminal && events.length > 0 && (
          <ThinkingRow />
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <button
          onClick={onClose}
          className="agent-cta-ghost"
          style={{ height: 32, fontSize: 11.5, fontWeight: 600, fontFamily: FF }}
        >
          {done ? "Close" : "Hide"}
        </button>
      </div>
    </>
  )
}

// Trailing "..." animation that lives at the end of the active step label.
function TypingDots() {
  return (
    <span style={{
      display: "inline-flex", alignItems: "flex-end", gap: 2,
      marginLeft: 4,
      transform: "translateY(-1px)",
    }}>
      <span className="agent-td" style={{ animationDelay: "0ms" }}>·</span>
      <span className="agent-td" style={{ animationDelay: "150ms" }}>·</span>
      <span className="agent-td" style={{ animationDelay: "300ms" }}>·</span>
    </span>
  )
}

// A low-key "agent is thinking" row that lives at the bottom of the timeline
// while we're waiting for the next event to arrive.
function ThinkingRow() {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "0 0 2px 0",
      position: "relative",
      animation: "agent-fade 0.3s ease both",
    }}>
      <div style={{
        width: 15, height: 15, borderRadius: "50%",
        display: "flex", alignItems: "center", justifyContent: "center",
        border: "1px dashed #e4e4e7",
        flexShrink: 0,
      }} />
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 3,
        fontSize: 11, color: "#a1a1aa", fontStyle: "italic", fontFamily: FF,
      }}>
        thinking
        <span className="agent-td" style={{ animationDelay: "0ms" }}>·</span>
        <span className="agent-td" style={{ animationDelay: "180ms" }}>·</span>
        <span className="agent-td" style={{ animationDelay: "360ms" }}>·</span>
      </span>
    </div>
  )
}

// Render each SSE payload as it comes off the wire — no renaming, no tool
// label mapping. We only tag the row kind so the terminal states (done /
// cancelled / error) can be styled differently.
function formatProgressEvent(ev) {
  const type = ev?.type || ""
  const label = [ev?.text, ev?.message, ev?.label, ev?.tool, ev?.status]
    .map(v => (v ?? "").toString().trim())
    .find(Boolean) || ""
  if (!label) return null
  const kind =
    type === "done"      ? "done" :
    type === "cancelled" ? "cancelled" :
    type === "error"     ? "error" :
    "step"
  return { label, kind }
}

// ──────────────────────────────────────────────────────────────────────────
// OptimisedSummary — text-first readout of what the agent did.
// ──────────────────────────────────────────────────────────────────────────
function OptimisedSummary({ percentage, matchedCount, totalCount, hardCovered, hardMissing, changes }) {
  const nameOf = (k) =>
    typeof k === "string" ? k : (k?.keyword || k?.name || k?.label || k?.skill || k?.text || "")
  const coveredNames = hardCovered.map(nameOf).filter(Boolean)
  const missingNames = hardMissing.map(nameOf).filter(Boolean)
  return (
    <>
      <Section label="Match score">
        <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
          <span style={{
            fontSize: 36, fontWeight: 800, color: "#18181b",
            letterSpacing: "-0.03em", lineHeight: 1,
          }}>{percentage}</span>
          <span style={{ fontSize: 15, fontWeight: 700, color: "#86909c" }}>%</span>
        </div>
        <div style={{ marginTop: 4, fontSize: 11.5, color: "#86909c", fontWeight: 500 }}>
          {matchedCount} of {totalCount} keywords present
        </div>
      </Section>

      {coveredNames.length > 0 && (
        <Section label={`Hard skills covered · ${coveredNames.length}`}>
          <ChipList items={coveredNames} tone="covered" />
        </Section>
      )}

      {missingNames.length > 0 && (
        <Section label={`Hard skills missing · ${missingNames.length}`}>
          <ChipList items={missingNames} tone="missing" />
        </Section>
      )}

      {changes.length > 0 && (
        <Section label={`What changed · ${changes.length}`}>
          <ul style={{ margin: 0, paddingLeft: 14, listStyleType: "disc" }}>
            {changes.slice(0, 6).map((c, i) => (
              <li key={i} style={{
                fontSize: 12, color: "#3f3f46", lineHeight: 1.5,
                marginBottom: i < Math.min(changes.length, 6) - 1 ? 6 : 0,
              }}>
                {c?.reason || c?.location || "Edit"}
              </li>
            ))}
            {changes.length > 6 && (
              <li style={{ fontSize: 11, color: "#a1a1aa", lineHeight: 1.5, marginTop: 4, listStyleType: "none", marginLeft: -14 }}>
                +{changes.length - 6} more edits
              </li>
            )}
          </ul>
        </Section>
      )}
    </>
  )
}

function DefaultSummary() {
  return (
    <Section label="Original resume">
      <p style={{
        margin: 0, fontSize: 12, color: "#3f3f46", lineHeight: 1.55,
      }}>
        Your current uploaded resume, submitted as-is. No tailoring, no
        rewrites — fastest option when the role already matches your profile.
      </p>
    </Section>
  )
}

function Section({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{
        fontSize: 9.5, fontWeight: 700, color: "#a1a1aa",
        textTransform: "uppercase", letterSpacing: "0.1em",
      }}>
        {label}
      </div>
      <div>{children}</div>
    </div>
  )
}

function ChipList({ items, tone = "covered" }) {
  const covered = tone === "covered"
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
      {items.map((item, i) => (
        <span key={i} style={{
          display: "inline-flex", alignItems: "center",
          padding: "4px 10px",
          background: covered ? "#f4f4f5" : "#ffffff",
          border: covered ? "none" : "1px dashed #d4d4d8",
          color: covered ? "#18181b" : "#86909c",
          borderRadius: 9999,
          fontSize: 11, fontWeight: 500,
          fontFamily: FF,
          whiteSpace: "nowrap",
        }}>
          {item}
        </span>
      ))}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// ResumePreviewCard — renders the PDF inline in the panel. Click to open
// the fullscreen modal.
// ──────────────────────────────────────────────────────────────────────────
function ResumePreviewCard({ url, onOpen }) {
  return (
    <div
      role="button"
      tabIndex={0}
      className="agent-resume-card"
      onClick={onOpen}
      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen?.() } }}
      aria-label="Open resume in full screen"
      style={{
        position: "relative",
        display: "block",
        width: "100%",
        flexShrink: 0,
        padding: 10,
        background: "#ffffff",
        border: "1px solid #ececec",
        borderRadius: 12,
        cursor: "zoom-in",
        overflow: "hidden",
      }}
    >
      <ResumeCanvas url={url} loading={<ResumeShimmer />} />
      <span className="agent-resume-expand" aria-hidden="true">
        <Maximize2 size={14} strokeWidth={2.2} />
      </span>
    </div>
  )
}

// A resume-shaped skeleton shown while the PDF is being fetched/decoded.
// Mimics the layout of a real resume page: name header, contact line,
// section headings and bulleted body lines — all shimmering.
function ResumeShimmer() {
  const sections = [
    { heading: 70, lines: [92, 86, 74] },
    { heading: 55, lines: [88, 94, 70, 82] },
    { heading: 60, lines: [78, 90] },
  ]
  return (
    <div
      aria-label="Loading resume"
      style={{
        width: "100%",
        aspectRatio: "8.5 / 11",
        background: "#ffffff",
        borderRadius: 6,
        boxShadow: "0 1px 2px rgba(18,18,18,0.04)",
        padding: "7% 8%",
        display: "flex", flexDirection: "column", gap: 14,
      }}
    >
      {/* Name + contact header */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "center", paddingBottom: 10, borderBottom: "1px solid #f1f3f5" }}>
        <span className="agent-resume-shimmer" style={{ width: "42%", height: 14, borderRadius: 3 }} />
        <span className="agent-resume-shimmer" style={{ width: "62%", height: 6, borderRadius: 2 }} />
      </div>

      {sections.map((s, i) => (
        <div key={i} style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          <span className="agent-resume-shimmer" style={{ width: `${s.heading}%`, height: 8, borderRadius: 2 }} />
          <span style={{ height: 1, background: "#f1f3f5", margin: "1px 0 3px" }} />
          {s.lines.map((w, j) => (
            <span
              key={j}
              className="agent-resume-shimmer"
              style={{ width: `${w}%`, height: 5, borderRadius: 2 }}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// ResumeFullscreenModal — fullscreen overlay showing the full resume PDF.
// ──────────────────────────────────────────────────────────────────────────
function ResumeFullscreenModal({ url, title, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.() }
    window.addEventListener("keydown", onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      window.removeEventListener("keydown", onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [onClose])

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(10,10,12,0.72)",
        backdropFilter: "blur(6px)",
        zIndex: 10000,
        display: "flex", flexDirection: "column",
        animation: "agent-fade 0.18s ease both",
        fontFamily: FF,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 20px",
          color: "#ffffff",
        }}
      >
        <div style={{
          fontSize: 13, fontWeight: 600,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          maxWidth: "70%",
        }}>
          {title}
        </div>
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            width: 34, height: 34, borderRadius: 10,
            background: "rgba(255,255,255,0.08)", border: "none",
            color: "#ffffff", cursor: "pointer",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            transition: "background 0.12s ease",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.16)" }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.08)" }}
        >
          <X size={18} strokeWidth={2.2} />
        </button>
      </div>

      <div
        onClick={e => e.stopPropagation()}
        style={{
          flex: 1, minHeight: 0,
          overflow: "auto",
          padding: "8px 24px 40px",
        }}
      >
        <div style={{ maxWidth: "min(9in, 100%)", margin: "0 auto" }}>
          <ResumeCanvas url={url} />
        </div>
      </div>
    </div>,
    document.body,
  )
}
